import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, Copy, Check, Eye, EyeOff } from 'lucide-react';
import type {
  AppCredentialDto,
  AppPlatform,
  CreateAppCredentialInput,
} from '@forest/contracts';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Dialog } from '@/components/ui/Dialog';
import { FormField } from '@/components/ui/FormField';
import { Table, Th, Td } from '@/components/ui/Table';
import { EmptyState } from '@/components/ui/EmptyState';
import { api, apiError } from '@/lib/api-client';
import { useUiStore } from '@/stores/uiStore';

type ListResponse = { items: AppCredentialDto[] };

function MaskedCredential({ value, copyLabel, copiedLabel, revealLabel, hideLabel }: {
  value: string;
  copyLabel: string;
  copiedLabel: string;
  revealLabel: string;
  hideLabel: string;
}) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const masked = value.length > 4 ? `••••${value.slice(-4)}` : '••••';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable (insecure context); ignore.
    }
  };

  return (
    <div className="flex items-center gap-2">
      <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">
        {revealed ? value : masked}
      </code>
      <button
        type="button"
        onClick={() => setRevealed((r) => !r)}
        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
        title={revealed ? hideLabel : revealLabel}
        aria-label={revealed ? hideLabel : revealLabel}
      >
        {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
      <button
        type="button"
        onClick={copy}
        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
        title={copied ? copiedLabel : copyLabel}
        aria-label={copied ? copiedLabel : copyLabel}
      >
        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function AppCredentialsPage() {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);

  const [platform, setPlatform] = useState<AppPlatform>('ANDROID');
  const [version, setVersion] = useState('');
  const [label, setLabel] = useState('');
  // Holds the just-created credential so the plaintext can be copied once.
  const [created, setCreated] = useState<AppCredentialDto | null>(null);
  const [createdCopied, setCreatedCopied] = useState(false);

  const listQ = useQuery<ListResponse>({
    queryKey: ['admin', 'app-credentials'],
    queryFn: () => api.get('admin/app-credentials').json<ListResponse>(),
  });

  const generate = useMutation({
    mutationFn: () => {
      const body: CreateAppCredentialInput = {
        platform,
        version: version.trim(),
        ...(label.trim() ? { label: label.trim() } : {}),
      };
      return api.post('admin/app-credentials', { json: body }).json<AppCredentialDto>();
    },
    onSuccess: (dto) => {
      qc.invalidateQueries({ queryKey: ['admin', 'app-credentials'] });
      setCreated(dto);
      setVersion('');
      setLabel('');
      pushToast({ variant: 'success', message: t('admin:appCredentials.generate.success') });
    },
    onError: async (e) => {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const setStatus = useMutation({
    mutationFn: (vars: { id: string; status: 'ACTIVE' | 'DEPRECATED' }) =>
      api
        .patch(`admin/app-credentials/${vars.id}`, { json: { status: vars.status } })
        .json<AppCredentialDto>(),
    onSuccess: (_dto, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'app-credentials'] });
      pushToast({
        variant: 'success',
        message:
          vars.status === 'DEPRECATED'
            ? t('admin:appCredentials.deprecateSuccess')
            : t('admin:appCredentials.reactivateSuccess'),
      });
    },
    onError: async (e) => {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const onDeprecate = (id: string) => {
    if (!window.confirm(t('admin:appCredentials.confirm.deprecate'))) return;
    setStatus.mutate({ id, status: 'DEPRECATED' });
  };

  const copyCreated = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.credential);
      setCreatedCopied(true);
      setTimeout(() => setCreatedCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const items = listQ.data?.items ?? [];

  return (
    <div className="animate-fade-in mx-auto max-w-5xl space-y-6 py-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-forest-100 text-forest-700">
          <KeyRound className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {t('admin:appCredentials.title')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('admin:appCredentials.subtitle')}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
            <Plus className="h-4 w-4" /> {t('admin:appCredentials.generate.title')}
          </h2>
        </CardHeader>
        <CardBody>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              generate.mutate();
            }}
            className="grid grid-cols-1 gap-3 sm:grid-cols-4 sm:items-end"
          >
            <FormField label={t('admin:appCredentials.generate.platform')} htmlFor="ac-platform">
              <Select
                id="ac-platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value as AppPlatform)}
              >
                <option value="ANDROID">{t('admin:appCredentials.platform.ANDROID')}</option>
                <option value="IOS">{t('admin:appCredentials.platform.IOS')}</option>
              </Select>
            </FormField>
            <FormField label={t('admin:appCredentials.generate.version')} htmlFor="ac-version">
              <Input
                id="ac-version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder={t('admin:appCredentials.generate.versionPlaceholder')}
                maxLength={32}
                autoComplete="off"
              />
            </FormField>
            <FormField label={t('admin:appCredentials.generate.label')} htmlFor="ac-label">
              <Input
                id="ac-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t('admin:appCredentials.generate.labelPlaceholder')}
                maxLength={120}
                autoComplete="off"
              />
            </FormField>
            <Button type="submit" loading={generate.isPending} disabled={!version.trim()}>
              {t('admin:appCredentials.generate.submit')}
            </Button>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          {items.length === 0 ? (
            <div className="p-6">
              <EmptyState title={t('admin:appCredentials.empty')} />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('admin:appCredentials.table.platform')}</Th>
                  <Th>{t('admin:appCredentials.table.version')}</Th>
                  <Th>{t('admin:appCredentials.table.label')}</Th>
                  <Th>{t('admin:appCredentials.table.status')}</Th>
                  <Th>{t('admin:appCredentials.table.credential')}</Th>
                  <Th>{t('admin:appCredentials.table.createdAt')}</Th>
                  <Th>{t('admin:appCredentials.table.createdBy')}</Th>
                  <Th>{t('admin:appCredentials.table.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="dark:bg-slate-900">
                    <Td className="dark:text-slate-100">
                      {t(`admin:appCredentials.platform.${c.platform}`)}
                    </Td>
                    <Td className="dark:text-slate-100">{c.version}</Td>
                    <Td className="dark:text-slate-100">{c.label ?? '-'}</Td>
                    <Td>
                      <Badge tone={c.status === 'ACTIVE' ? 'green' : 'red'}>
                        {t(`admin:appCredentials.status.${c.status}`)}
                      </Badge>
                    </Td>
                    <Td>
                      <MaskedCredential
                        value={c.credential}
                        copyLabel={t('admin:appCredentials.action.copy')}
                        copiedLabel={t('admin:appCredentials.action.copied')}
                        revealLabel={t('admin:appCredentials.action.reveal')}
                        hideLabel={t('admin:appCredentials.action.hide')}
                      />
                    </Td>
                    <Td className="whitespace-nowrap dark:text-slate-100">
                      {new Date(c.createdAt).toLocaleString()}
                    </Td>
                    <Td className="dark:text-slate-100">
                      {c.createdBy ? (c.createdBy.cod ?? c.createdBy.fullName) : '-'}
                    </Td>
                    <Td>
                      {c.status === 'ACTIVE' ? (
                        <Button
                          variant="danger"
                          size="sm"
                          loading={setStatus.isPending}
                          onClick={() => onDeprecate(c.id)}
                        >
                          {t('admin:appCredentials.action.deprecate')}
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          loading={setStatus.isPending}
                          onClick={() => setStatus.mutate({ id: c.id, status: 'ACTIVE' })}
                        >
                          {t('admin:appCredentials.action.reactivate')}
                        </Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Dialog
        open={created !== null}
        onClose={() => setCreated(null)}
        title={t('admin:appCredentials.created.title')}
      >
        {created ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {t('admin:appCredentials.created.note')}
            </p>
            <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
              <code className="flex-1 break-all font-mono text-xs text-slate-800 dark:text-slate-100">
                {created.credential}
              </code>
              <Button variant="secondary" size="sm" onClick={copyCreated}>
                {createdCopied ? (
                  <Check className="mr-1 h-4 w-4 text-emerald-600" />
                ) : (
                  <Copy className="mr-1 h-4 w-4" />
                )}
                {createdCopied
                  ? t('admin:appCredentials.created.copied')
                  : t('admin:appCredentials.created.copy')}
              </Button>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setCreated(null)}>
                {t('admin:appCredentials.created.done')}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
